import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProgramApplicationDialog({ open, onOpenChange, program }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    age: "",
    city: "",
    reason: "",
    referred_by: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!program) return;
    setLoading(true);
    try {
      const payload = {
        program: program.slug,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        age: form.age ? Number(form.age) : undefined,
        city: form.city || undefined,
        reason: form.reason,
        referred_by: form.referred_by || undefined,
      };
      await api.post("/program-applications", payload);
      toast.success(`Application received for ${program.name}. We'll be in touch soon.`);
      onOpenChange(false);
      setForm({ full_name: "", email: "", phone: "", age: "", city: "", reason: "", referred_by: "" });
    } catch (err) {
      toast.error("Could not submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="program-app-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-clay/20 bg-oat">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-ink">
            Apply — {program?.name}
          </DialogTitle>
          <DialogDescription className="text-ink-muted">
            All applications are reviewed within 5 business days. Fields marked * are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="field-label">Full name *</label>
            <input required data-testid="app-full-name" className="field-input" value={form.full_name} onChange={set("full_name")} />
          </div>
          <div>
            <label className="field-label">Email *</label>
            <input required type="email" data-testid="app-email" className="field-input" value={form.email} onChange={set("email")} />
          </div>
          <div>
            <label className="field-label">Phone *</label>
            <input required data-testid="app-phone" className="field-input" value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <label className="field-label">Age</label>
            <input type="number" min="0" data-testid="app-age" className="field-input" value={form.age} onChange={set("age")} />
          </div>
          <div>
            <label className="field-label">City / neighborhood</label>
            <input data-testid="app-city" className="field-input" value={form.city} onChange={set("city")} />
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Why are you interested? *</label>
            <textarea required rows={4} data-testid="app-reason" className="field-input" value={form.reason} onChange={set("reason")} />
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Who referred you? (optional)</label>
            <input data-testid="app-referrer" className="field-input" value={form.referred_by} onChange={set("referred_by")} />
          </div>
          <div className="sm:col-span-2 mt-2 flex items-center justify-end gap-3">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="app-cancel" className="btn-ghost">Cancel</button>
            <button type="submit" disabled={loading} data-testid="app-submit" className="btn-primary">
              {loading ? "Submitting…" : "Submit Application"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
